import { useMemo } from "react";

import { isValidDate, useFormikWithDirtyForm } from "@shared";
import { startOfDay } from "date-fns";

import { nameof } from "@/common/helper/commonHelper";
import { useCreateExchangeMutation } from "@/pages/exchange/hooks/useCreateExchangeMutation";
import { useExchangeableAppointmentSelection } from "@/pages/exchange/hooks/useExchangeableAppointmentSelection";
import { useTranslation } from "@/translations";

import { NewExchangeRequestFormValues } from "../NewExchangeRequestDialog.types";
import { newExchangeRequestValidationSchema } from "../NewExchangeRequestDialog.utils";

interface Params {
  onClose: () => void;
}

/**
 * Form state of the "Neue Tauschanfrage" dialog. The selected Termin and the requested Zwecke live in
 * the formik values, so the picker and the chips are plain controlled views of the form.
 */
export function useNewExchangeRequest({ onClose }: Params) {
  const { t } = useTranslation();
  const createMutation = useCreateExchangeMutation();

  const validationSchema = useMemo(() => newExchangeRequestValidationSchema(t), [t]);
  const today = startOfDay(new Date());

  const formik = useFormikWithDirtyForm<NewExchangeRequestFormValues>({
    initialValues: { from: today, till: today, appointmentId: null, requestedPurposeKeys: [], note: "" },
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    onSubmit: async (values) => {
      if (values.appointmentId === null) {
        return;
      }
      const note = values.note.trim();
      try {
        await createMutation.mutateAsync(
          {
            appointmentId: values.appointmentId,
            requestedPurposeKeys: values.requestedPurposeKeys,
            note: note === "" ? null : note,
          },
          { onSuccess: onClose }
        );
      } catch {
        // Reported by the global error handling; the dialog stays open so the input is kept.
      }
    },
  });

  const selection = useExchangeableAppointmentSelection(formik);
  const { appointmentId, requestedPurposeKeys } = formik.values;

  // Most requests offer an appointment on a single day, so the end date (Datum-Bis) takes over the start
  // date (Datum-Von) even after it was changed by hand; a longer range is set by editing the end date afterwards.
  const handleFromChange = (from: Date | null) => {
    if (isValidDate(from)) {
      void formik.setFieldValue(nameof<NewExchangeRequestFormValues>("till"), from, formik.submitCount > 0);
    }
    selection.clearAppointment();
  };

  // The button only becomes active once exactly one own appointment (eigener Dienst) and at least one
  // wanted purpose (gesuchter Dienst) are selected. The Yup schema and the per-section marking stay as the
  // safety net for the paths without a selected appointment (and for anything that later loosens this gate).
  const submitDisabled = appointmentId === null || requestedPurposeKeys.length === 0 || createMutation.isPending;

  return {
    formik,
    ...selection,
    handleFromChange,
    submitDisabled,
  };
}
